import {action, autorun, observable, runInAction} from 'mobx';
import searchStore from '../../../stores/SearchStore';
import userStore from '../../../stores/UserStore';
import {instruments, instrumentsForUsers, keys, lessonCategories, levels, PROJECT_SKILLS} from '../../../constants/constants';
import {SearchState} from '../../../constants/enums';
import {translate} from '../../../utils/localizationUtility';
import projectStore from '../project/store';
import {getUserInstruments, getUserMainInstrument, isAdmin} from "../../../utils/userUtility";

class DiscoverStore {
    @observable isSetFilters = false;
    @observable instrumentsIndex = 0;
    @observable levelIndex = 0;
    @observable categoryIndex = 0;
    @observable keyIndex = 0;
    @observable sortByIndex = 0;
    @observable skillsIndex = 0;
    @observable searchState = SearchState.discovery;
    @observable filterInitDone = false;
    @observable instrumentsList = [];
    @observable levelsList = [];
    @observable lessonCategoriesList = [];
    @observable keysList = [];
    @observable sortByList = [];
    @observable skillsList = [];
    @observable activeDiscoverCollection = {};
    @observable showGalleries = true;
    @observable multiFilter = null;
    @observable label = null;

    constructor() {
        autorun(() => {
            // console.log('initializedLocale: ' + userStore.initializedLocale + ' id: ' + userStore.user.id + ' isAuth:' + userStore.isAuthenticated);
            if (userStore.initializedLocale && userStore.user.id && !this.filterInitDone) {
                runInAction(() => {
                    this.initFilters();
                });
            }
            if (projectStore.isLessonPlansLoaded) {
                runInAction(() => {
                    this.reduceInstrumentsList();
                    this.resetList(this.instrumentsList);

                    // convert this.instrumentsList to a boolean array true where the instrument is selected
                    if (!this.multiFilter) {
                        this.multiFilter = {
                            category: this.lessonCategoriesList[this.categoryIndex],
                            instrument: this.instrumentsList[this.instrumentsIndex],
                            level: this.levelsList[this.levelIndex],
                            musicalKey: this.keysList[this.keyIndex],
                            skill: this.skillsList[this.skillsIndex],
                            sortBy: this.sortByList[this.sortByIndex],
                            label: this.label
                        };
                    }
                    if (!isAdmin() && this.searchState === SearchState.discovery) {
                        this.onChangeInstrument(getUserInstruments(), false);
                    }
                });
            }
        });
    }

    @action initFilters = () => {
        this.instrumentsList = [
            {
                label: `${translate('general.all')} ${translate('library.lessons.instruments.plural')}`,
                isAll: true
            }, ...(isAdmin() ? instruments : instrumentsForUsers).map((item) => {
                return {
                    label: translate(`upload.form.instruments.${item.name}`),
                    value: item.name,
                    selected: false
                };
            })
        ];

        this.levelsList = [
            {
                label: `${translate('general.all')} ${translate('library.lessons.level.plural')}`,
                isAll: true
            }, ...levels.map((item) => {
                return {
                    label: translate(`constants.level.${item.name}`),
                    value: item.name,
                    selected: false
                };
            })
        ];

        this.lessonCategoriesList = [
            {
                label: `${translate('general.all')} ${translate('library.lessons.category.plural')}`,
                isAll: true
            }, ...lessonCategories.map((item) => {
                return {
                    label: translate(`constants.lessonCategory.${item.name}`),
                    value: item.name,
                    selected: false
                };
            })
        ];

        this.keysList = [
            {
                label: `${translate('general.all')} ${translate('library.lessons.key.plural')}`,
                isAll: true
            }, ...keys.map((item) => {
                return {
                    label: item.name,
                    value: item.name,
                    selected: false
                };
            })
        ];
        this.sortByList = [
            ...projectStore.getGlobalLessonsSortByList().map((item) => {
                return {
                    label: translate(`constants.sortBy.${item.name}`),
                    value: item.name,
                    action: item.action
                };
            })
        ];

        this.skillsList = [
            {
                label: `${translate('general.all')} ${translate('library.lessons.skills')}`,
                isAll: true
            },
            ...PROJECT_SKILLS.map((item) => {
                return {
                    label: translate(`skills.list.${item.type}`),
                    value: item.type,
                    selected: false
                };
            })
        ];
        this.filterInitDone = true;
    };

    reduceInstrumentsList = () => {
        const lessonPlansInstruments = projectStore.getLessonPlansInstruments();
        if (this.instrumentsList) {
            this.instrumentsList.replace(
                this.instrumentsList.filter(instrument => {
                    return ((lessonPlansInstruments.findIndex(planInstrument => planInstrument.name === instrument.value) !== -1) || instrument.isAll);
                })
            );
        }
    }

    @action setShowGalleries = (val) => {
        this.showGalleries = val
    }

    @action setSearchState = (state) => {
        this.searchState = state;
        // this.showGalleries = false;
    };

    @action onMultiSelect = (stateArray = [], filteredKey, filterList = [], updateShowGalleryState = false) => {
        if (updateShowGalleryState) {
            this.showGalleries = false;
        }

        let selectedValues = filterList.filter((item, index) => {
            filterList[index].selected = stateArray[index];
            if (stateArray[index]) {
                return true;
            }
        })
        if (!selectedValues.length) {
            selectedValues = filterList[0];
        }

        this.multiFilter = {
            ...this.multiFilter,
            [filteredKey]: selectedValues
        }

        this.isSetFilters = true;
        this.updateMultiSearchFilter();
    }

    @action onChangeLabel = (label, updateShowGalleryState = false) => {
        if (updateShowGalleryState) {
            this.showGalleries = true;
        }
        this.isSetFilters = true;
        this.label = label;
        if (this.multiFilter) {
            this.multiFilter.label = label;
            this.updateMultiSearchFilter();
        }
    }

    @action onChangeCategory = (filterValue, updateShowGalleryState = false) => {
        this.resetList(this.lessonCategoriesList);
        if (updateShowGalleryState) {
            this.showGalleries = false;
        }
        const stateFilter = this.lessonCategoriesList.map(item => {
            return item.value === filterValue
        });
        this.onMultiSelect(stateFilter, "category", this.lessonCategoriesList, false);

        this.updateMultiSearchFilter();
    };

    @action onChangeLevel = (filterValue, updateShowGalleryState = false) => {
        this.resetList(this.levelsList);
        if (updateShowGalleryState) {
            this.showGalleries = false;
        }
        const stateFilter = this.levelsList.map(item => item.value === filterValue);
        this.onMultiSelect(stateFilter, "level", this.levelsList, false);

        this.updateMultiSearchFilter();
    };

    @action onChangeInstrument = (filterValue, updateShowGalleryState = false) => {
        if (filterValue.length < 0) {
            filterValue = [filterValue];
        }
        this.resetList(this.instrumentsList);
        if (updateShowGalleryState) {
            this.showGalleries = true;
        }
        const stateFilter = this.instrumentsList.map(item => filterValue.includes(item.value));
        this.onMultiSelect(stateFilter, "instrument", this.instrumentsList, false);
    };

    @action onChangeSortBy = ({value}, index, state) => {
        this.sortByIndex = index;
        this.showGalleries = false;
        this.updateMultiSearchFilter();
    };

    @action setDefaultFilters = () => {
        this.instrumentsIndex = this.getUserInstrumentIndex();
        this.levelIndex = 0;
        this.categoryIndex = 0;
        this.keyIndex = 0;
        this.sortByIndex = 0;

        this.resetList(this.instrumentsList);
        this.resetList(this.levelsList);
        this.resetList(this.lessonCategoriesList);
        this.resetList(this.keysList);
        this.instrumentsList[0].selected = true;
        this.levelsList[0].selected = true;
        this.lessonCategoriesList[0].selected = true;
        this.keysList[0].selected = true;

        this.isSetFilters = false;
    };


    @action resetFilters = () => {
        if (this.instrumentsList) {
            this.setDefaultFilters();
            this.updateMultiSearchFilter();
        }
    };

    updateMultiSearchFilter = () => {
        if (this.multiFilter) {
            searchStore.updateSearchFilter(this.multiFilter, this.searchState);
            searchStore.fetchItems(this.searchState, false);
        }
    }

    resetList = (list) => {
        list.forEach(obj => obj.selected = false);
    };

    setPublic = (isPublic) => {
        this.searchState = isPublic ? SearchState.discoveryPublic : SearchState.discovery;
    };

    getUserInstrumentIndex = () => {
        if (!getUserMainInstrument()) {
            return 0;
        }
        const index = this.instrumentsList.findIndex(instrument => instrument.label === getUserMainInstrument())
        return index === -1 ? 0 : index;
    }

    @action setActiveDiscoverCollection = (collection) => {
        this.activeDiscoverCollection = collection
    }
}

const discoverStore = new DiscoverStore();
export default discoverStore;
